import type { EventsByDateWithLayout } from "../types";

export interface HiddenEventsFlags {
  hasEarlierHiddenEvents: boolean;
  hasLaterHiddenEvents: boolean;
}

/**
 * Returns flags indicating whether any events fall entirely outside the
 * visible per-day window defined by `dayStartMinutes`/`dayEndMinutes`.
 *
 * An event counts as hidden only if it does not overlap the visible window;
 * events that extend into the window (or that are clipped at top/bottom) are
 * still considered visible.
 */
export const getHiddenEventsFlags = (
  eventsByDate: EventsByDateWithLayout,
  dayStartMinutes: number,
  dayEndMinutes: number
): HiddenEventsFlags =>
  Object.keys(eventsByDate).reduce<HiddenEventsFlags>(
    (acc, timestampKey) => {
      const dayStartMs = Number(timestampKey);
      const viewportStartMs = dayStartMs + dayStartMinutes * 60_000;
      const viewportEndMs = dayStartMs + dayEndMinutes * 60_000;
      const events = eventsByDate[dayStartMs] ?? [];
      return events.reduce<HiddenEventsFlags>((innerAcc, event) => {
        const eventStartMs = event.startTimeInMS;
        const eventEndMs = eventStartMs + (event.actualDuration ?? event.durationInMinutes) * 60_000;
        return {
          hasEarlierHiddenEvents: innerAcc.hasEarlierHiddenEvents || eventEndMs <= viewportStartMs,
          hasLaterHiddenEvents: innerAcc.hasLaterHiddenEvents || eventStartMs >= viewportEndMs,
        };
      }, acc);
    },
    { hasEarlierHiddenEvents: false, hasLaterHiddenEvents: false }
  );
